import React, { useState } from 'react';
import { isNotNilOrEmpty } from 'ramda-adjunct';
import { find, prop, propEq, trim } from 'ramda';
import RemoveByEmailInvitationMutation from '../_graphql/mutations/RemoveByEmailInvitationMutation';
import RemoveUserMutation from '../_graphql/mutations/RemoveUserMutation';
import SendInvitationEmailMutation from '../_graphql/mutations/SendInvitationEmailMutation';
import { errorNotification, successNotification } from '../utils/messageMutation';
import RemoveUserFromTeamMutation from '../_graphql/mutations/RemoveUserFromTeamMutation';
import CreateTeamMutation from '../_graphql/mutations/teams/CreateTeamMutation';
import useActionGuard from './useActionGuard';

const useUserTableActions = ({
  reFetch,
  userRefetch,
  isAdministration,
  teamId
}) => {
  const [userModalIsOpen, setUserModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [createTeamModalIsOpen, setCreateTeamModalIsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState();
  const { loading, run } = useActionGuard();

  const onRemoveUser = (user) => {
    setSelectedUser(user);
    setDeleteModalIsOpen(true);
  };

  const handleCloseModal = (refetch) => {
    setUserModalIsOpen(false);
    setDeleteModalIsOpen(false);
    setSelectedUser(null);

    if (refetch) {
      reFetch();
    }
  };

  const onAddUser = () => setUserModalIsOpen(true);

  const onEditUser = (user) => {
    setSelectedUser(user);
    setUserModalIsOpen(true);
  };

  const onResendInvitation = (email) => SendInvitationEmailMutation({ email }, (ok, error) => {
    if (ok && !error) {
      successNotification(`Une invitation a été renvoyée à ${email}.`);
    } else {
      errorNotification(`L'invitation n'a pas pu être renvoyée à ${email}.`);
    }
  });
    
  const onConfirmDelete = (user) => {
    const callback = (ok, error, userExist) => {
      const typeMsg = userExist ? 'utilisateur' : 'invitation';
      const actionMsg = isAdministration ? 'supprimé' : 'retiré de l\'équipe';
      if (ok && !error) {
        successNotification(`L\'${typeMsg} a été ${actionMsg}.`);
        reFetch();
      } else {
        errorNotification(`L\'${typeMsg} n\'a pas pu être ${actionMsg}.`);
      }
      setDeleteModalIsOpen(false);
      setSelectedUser(null);
    };

    const userId = prop('id', user);

    run(`delete-user-${userId}`, (done) => {
      if (isNotNilOrEmpty(userId)) {
        if (isAdministration) {
          RemoveUserMutation({ userId }, done);
        } else {
          RemoveUserFromTeamMutation({ teamId, userId }, done);
        }
      } else {
        RemoveByEmailInvitationMutation({ email: prop('email', user) }, done);
      }
    }, (ok, error) => callback(ok, error, isNotNilOrEmpty(userId)));
  };

  const onCreateTeam = teamName => {
    run(`create-team-${teamId}`, (done) => {
      CreateTeamMutation({ name: trim(teamName) }, done);
    }, (ok, error) => {
      if (ok && !error) {
        userRefetch();
        reFetch();
        setCreateTeamModalIsOpen(false);
        successNotification('L\'équipe a été créée !', 'Vous pouvez maintenant inviter vos collaborateurs.');
      } else {
        errorNotification();
      }
    });
  };

  const selectUserByEmail = (users) => (email) => {
    const selectedUser = find(propEq(email, 'email'), users);
    onEditUser(selectedUser);
  };

  return {
    userModalIsOpen,
    deleteModalIsOpen,
    createTeamModalIsOpen,
    selectedUser,
    handleCloseModal,
    onAddUser,
    onEditUser,
    onRemoveUser,
    onResendInvitation,
    onConfirmDelete,
    selectUserByEmail,
    setCreateTeamModalIsOpen,
    onCreateTeam,
    loading
  };
};

export default useUserTableActions;